import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFoodItems, getMyEvents, createOrder } from '../../services/api';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import useToast from '../../hooks/useToast';
import { TrashIcon, PlusIcon, MinusIcon, ShoppingCartIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';

const CreateOrder = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [cart, setCart] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');

  // ✅ React Query: Fetch food items
  const { 
    data: foodItems = [], 
    isLoading: foodLoading 
  } = useApiQuery(
    ['food-items'],
    () => getFoodItems().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError('Failed to load food items'),
    }
  );

  // ✅ React Query: Fetch events
  const { 
    data: events = [], 
    isLoading: eventsLoading,
    refetch: refetchEvents,
  } = useApiQuery(
    ['vendor-events'],
    () => getMyEvents().then(res => res.data),
    {
      staleTime: 10 * 60 * 1000,
      onError: () => {
        console.error('Failed to fetch events');
        showError('Failed to load events');
      },
    }
  );

  // ✅ Auto-select first event when data loads
  useEffect(() => {
    if (events.length > 0 && !selectedEvent) {
      setSelectedEvent(events[0]._id);
    }
  }, [events, selectedEvent]);

  // ✅ React Query: Create order mutation
  const createOrderMutation = useApiMutation(
    (payload) => createOrder(payload).then(res => res.data),
    {
      onSuccess: (data) => {
        showSuccess('Order created');
        navigate(`/vendor/payment/${data._id}`);
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to create order');
      },
    }
  );

  // ✅ Function to safely get the quantity of an item directly from the cart
  const getItemQuantity = useCallback((itemId) => {
    const itemInCart = cart.find(i => i.foodItemId === itemId);
    return itemInCart ? itemInCart.quantity : 0;
  }, [cart]);

  // ✅ Add item to cart
  const addToCart = useCallback((item) => {
    setCart(prev => {
      const existing = prev.find(i => i.foodItemId === item._id);
      if (existing) {
        return prev.map(i => 
          i.foodItemId === item._id 
            ? { ...i, quantity: i.quantity + 1 } 
            : i
        );
      }
      return [...prev, { 
        foodItemId: item._id, 
        name: item.name, 
        price: item.price, 
        quantity: 1 
      }];
    });
  }, []);

  // ✅ Remove item from cart
  const removeFromCart = useCallback((id) => {
    setCart(prev => prev.filter(i => i.foodItemId !== id));
  }, []);

  // ✅ Update item quantity
  const updateQuantity = useCallback((id, delta) => {
    setCart(prev => prev.map(i => {
      if (i.foodItemId === id) {
        const newQty = Math.max(1, i.quantity + delta);
        return { ...i, quantity: newQty };
      }
      return i;
    }));
  }, []);

  // ✅ Calculate total
  const total = useMemo(() => {
    return cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  }, [cart]);

  // ✅ Handle proceed to payment
  const handleProceed = useCallback(() => {
    console.log('🔍 Proceed clicked - cart:', cart.length, 'selectedEvent:', selectedEvent);
    
    if (cart.length === 0) {
      showError('Add at least one item');
      return;
    }
    if (!selectedEvent) {
      showError('Please select an event');
      return;
    }

    const payload = {
      eventId: selectedEvent,
      items: cart.map(i => ({ 
        foodItemId: i.foodItemId, 
        quantity: i.quantity 
      })),
    };

    console.log('📦 Order payload:', payload);
    createOrderMutation.mutate(payload);
  }, [cart, selectedEvent, createOrderMutation, showError]);

  // ✅ Loading state
  if (foodLoading || eventsLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-[#708090] text-lg">
          <div className="inline-block w-8 h-8 border-2 border-[#1471b9] border-t-transparent rounded-full animate-spin mr-3"></div>
          Loading Menu...
        </div>
      </div>
    );
  }

  // ✅ Error state - No events
  if (events.length === 0 && !eventsLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-[15px] p-6 max-w-md text-center">
          <p className="text-yellow-800 text-sm font-semibold">⚠️ No Events Found</p>
          <p className="text-yellow-700 text-sm mt-2">
            Please contact admin to assign you to an event.
          </p>
        </div>
      </div>
    );
  }

  // ✅ Debug: Log current state
  console.log('🔍 Current state - cart:', cart.length, 'selectedEvent:', selectedEvent);

  const isProceedDisabled = createOrderMutation.isPending || cart.length === 0 || !selectedEvent;

  return (
    <div className="min-h-[calc(100vh-100px)] bg-[#f8f9fa] p-6 flex flex-col lg:flex-row gap-6">
      
      {/* LEFT: Menu Items */}
      <div className="flex-1 flex flex-col">
        <h1 className="text-2xl font-normal text-[#2b3445] mb-6">Create New Order</h1>
        
        {/* Event Selector */}
        <div className="bg-white rounded-[15px] border border-[#dae1e7] p-4 mb-4 shadow-sm">
          <label className="block text-sm font-semibold text-[#2b3445] mb-1">
            Select Event *
          </label>
          <select
            value={selectedEvent || ''}
            onChange={(e) => {
              console.log('📌 Event selected:', e.target.value);
              setSelectedEvent(e.target.value);
            }}
            className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
          >
            <option value="">-- Select Event --</option>
            {events.map(event => (
              <option key={event._id} value={event._id}>
                {event.name}
              </option>
            ))}
          </select>
          {selectedEvent && (
            <p className="text-xs text-green-600 mt-1">
              ✓ Event selected: {events.find(e => e._id === selectedEvent)?.name}
            </p>
          )}
        </div>
        
        <div className="bg-white rounded-[15px] border border-[#dae1e7] p-6 shadow-sm flex-1">
          <h2 className="text-lg font-medium text-[#2b3445] mb-6">Select Food Items</h2>
          
          {foodItems.length === 0 ? (
            <div className="text-center py-8 text-[#708090]">
              No food items available. Please add items first.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {foodItems.map(item => {
                const qty = getItemQuantity(item._id);
                const isInCart = qty > 0;

                return (
                  <div 
                    key={item._id}
                    className={`flex justify-between items-center p-4 rounded-[12px] border transition-all duration-200 
                      ${isInCart ? 'border-[#1471b9] bg-[#f8fcff] shadow-sm' : 'border-[#dae1e7] bg-white hover:border-[#cbd5e1]'}`}
                  >
                    <div>
                      <span className="font-medium text-[#2b3445] capitalize">{item.name}</span>
                      <div className="text-sm text-[#708090]">₹{item.price.toFixed(2)}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!isInCart ? (
                        <button
                          type="button"
                          onClick={() => addToCart(item)}
                          className="bg-[#1471b9] text-white px-4 py-1.5 rounded-[8px] text-sm font-bold hover:bg-[#125fa3] transition-colors flex items-center gap-1"
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                          Add
                        </button>
                      ) : (
                        <div className="flex items-center bg-[#1471b9] rounded-[8px] overflow-hidden h-[34px]">
                          <button 
                            type="button" 
                            onClick={() => qty === 1 ? removeFromCart(item._id) : updateQuantity(item._id, -1)} 
                            className="px-3 h-full text-white hover:bg-[#125fa3] transition-colors flex items-center justify-center"
                          >
                            <MinusIcon className="h-3.5 w-3.5" />
                          </button>
                          
                          <span className="w-8 text-center text-white font-bold text-sm select-none">{qty}</span>
                          
                          <button 
                            type="button" 
                            onClick={() => updateQuantity(item._id, 1)} 
                            className="px-3 h-full text-white hover:bg-[#125fa3] transition-colors flex items-center justify-center"
                          >
                            <PlusIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: Cart Sidebar */}
      <div className="w-full lg:w-[350px] flex flex-col gap-4">
        <div className="bg-white rounded-[15px] border border-[#dae1e7] p-6 shadow-sm sticky top-6">
          <div className="flex justify-between items-center border-b border-[#dae1e7] pb-3 mb-4">
            <div className="flex items-center gap-2">
              <ShoppingCartIcon className="h-5 w-5 text-[#1471b9]" />
              <h2 className="text-lg font-bold text-[#2b3445]">Cart</h2>
            </div>
            <span className="bg-[#f0f4f8] text-[#2b3445] px-2.5 py-0.5 rounded-full text-xs font-bold">
              {cart.length} items
            </span>
          </div>

          <div className="max-h-[400px] overflow-y-auto pr-1 space-y-3">
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-[#708090]">No items added.</p>
                <p className="text-xs text-[#708090] mt-1">Tap an item to start.</p>
              </div>
            ) : (
              cart.map(item => (
                <div key={item.foodItemId} className="flex justify-between items-center text-sm border-b border-[#f0f4f8] pb-2">
                  <div className="flex flex-col">
                    <span className="font-medium text-[#2b3445]">{item.name}</span>
                    <span className="text-[#708090] text-xs">x {item.quantity}</span>
                  </div>
                  <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-[#dae1e7] mt-4 pt-4 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[#2b3445] font-bold">Total</span>
              <div className="flex items-center text-xl font-bold text-[#2b3445]">
                <CurrencyRupeeIcon className="h-5 w-5 mr-0.5" />
                {total.toFixed(2)}
              </div>
            </div>
            
            <button
              onClick={handleProceed}
              disabled={isProceedDisabled}
              className="w-full bg-[#1471b9] text-white py-3 rounded-[10px] text-base font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {createOrderMutation.isPending ? 'Creating Order...' : 'Proceed to Payment'}
            </button>
            
            {/* Debug info */}
            {isProceedDisabled && (
              <p className="text-xs text-red-500 text-center">
                {!selectedEvent ? '⚠️ Please select an event' : 
                 cart.length === 0 ? '⚠️ Add items to cart' : 
                 createOrderMutation.isPending ? '⏳ Processing...' : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrder;